import { zodResolver } from "@hookform/resolvers/zod";
import {
  KikCustomerSendEmailVerificationFields,
  kikCustomerSendEmailVerificationFieldsSchema,
  KikCustomerSignUpFields as CustomerSignUpFields,
  kikCustomerSignUpFieldsSchema as customerSignUpFieldsSchema,
  KikCustomerSignUpWithActionCodeFields,
  KikEvaCustomerFields as EvaCustomerFields,
  kikEvaCustomerFieldsSchema as evaCustomerFieldsSchema,
  KikFirebaseCustomClaims as FirebaseCustomClaims,
  kikFirebaseCustomClaimsSchema as firebaseCustomClaimsSchema,
  KikSocialSignInKeys as SocialSignInKeys,
  KikUserZodSchemas as ZodSchemas,
} from "@kikocosmeticsorg/uc-api-nest-common-fe";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { pick } from "ramda";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { SafeParseSuccess, z } from "zod";

import ControlledPhoneInput from "~/components/form/controlled-phone-input";
import { useActionCodeContext } from "~/contexts/action-code";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import Logger from "~/services/logger/logger";
import { ApiUtils } from "~/shared/api/api-utils.class";
import { LoginSignUpOrigins } from "~/shared/user/login-sign-up-origins.enum";
import appRoutes from "~/utils/app-routes";
import AppRoutes from "~/utils/app-routes";
import CommonUtils from "~/utils/common-utils";
import Constants from "~/utils/constants";
import crossCountryUtils from "~/utils/cross-country-utils";

import ActionCodeDialog from "../common/action-code-dialog";
import Button from "../common/button";
import Checkbox, { CheckboxVariants } from "../form/checkbox";
import Errors from "../form/errors";
import InputField from "../form/input-field";
import inputFieldStyles from "../form/input-field.module.scss";
import InputFieldDateMui from "../form/input-field-date-mui";
import SelectGeneric from "../form/select-generic";
import CustomerFlags from "./customer-flags";
import styles from "./sign-up.module.scss";

const evaCustomerFieldsWithoutDOB = evaCustomerFieldsSchema.partial({ DateOfBirth: true });
export type EvaCustomerFieldsWithoutDOB = z.infer<typeof evaCustomerFieldsWithoutDOB>;

type Props = {
  customerDataSource?: EvaCustomerFieldsWithoutDOB;
};

// TODO: COMPLETE MIGRATION OF SCHEMAS USED HERE, TO REMOVE ALL DEPRECATED ONES FROM COMMON-FE
export default function SignUp(props: Props) {
  const t = useTranslations();
  const router = useRouter();
  const breakpoint = useBreakpoint();
  const [showPassword, setShowPassword] = useState<boolean>(!1);
  const [filledData, setFilledData] = useState<Omit<KikCustomerSignUpWithActionCodeFields, "procedure" | "actionCode">>(
    {} as KikCustomerSignUpWithActionCodeFields
  );

  const {
    authError,
    isLoading,
    firebaseUser,
    //
    autoLogin,
    login,
    sendEmailVerification,
    signUpCustomerWithActionCode,
    signUpSocial,
  } = useAuthContext();
  const socialKey: SocialSignInKeys = SocialSignInKeys[router.query.social as keyof typeof SocialSignInKeys];
  const {
    resetTimer,
    timeLeft,
    dialogOpenState: [isActionCodeDialogOpen, setIsActionCodeDialogOpen],
  } = useActionCodeContext();
  const initialCountry = crossCountryUtils.getCurrentCountryCode(router);
  const availableLanguages = crossCountryUtils.getAvailableUserLanguages(router);
  let inVals: Partial<EvaCustomerFields> = {};
  let hiddenFields: Partial<Record<keyof EvaCustomerFields, boolean>> = {};
  // Prefill values from outside, usually the thank-you page
  if (!!props.customerDataSource) {
    // evaCustomerFieldsSchema is basically all the signup fields, without password/confirmPassword/customerFlags
    const orderCustomerParseResult = evaCustomerFieldsWithoutDOB.safeParse(
      props.customerDataSource
    ) as SafeParseSuccess<CustomerSignUpFields>;
    hiddenFields = CommonUtils.mapObjectValues(orderCustomerParseResult.data, (value) => !!value);
    inVals = orderCustomerParseResult.data || {};
  }
  // Means I got here from a social login, but I don't have a user on EVA, so I proceed with a sign-up
  else if (socialKey && firebaseUser && firebaseUser.providerData?.[0].providerId !== "firebase") {
    Logger.instance.debug("SignUp: handling social intermediate registration", {
      firebaseUser,
      firebaseProvider: firebaseUser.providerData?.[0].providerId,
    });
    inVals = {
      FirstName: firebaseUser.displayName?.split(" ")[0],
      LastName: firebaseUser.displayName?.split(" ")[1],
      EmailAddress: firebaseUser.email!,
    };
  }

  const formMethods = useForm<CustomerSignUpFields>({
    ...Constants.USE_FORM_VALIDATION_DEFAULTS,
    defaultValues: {
      LanguageID: availableLanguages[0].value,
      ...inVals,
    },
    resolver: zodResolver(socialKey ? firebaseCustomClaimsSchema : customerSignUpFieldsSchema),
  });
  const formRef = useRef<HTMLFormElement>(null);

  const {
    control,
    register,
    handleSubmit,
    clearErrors,
    setError,
    formState: { errors, isSubmitting },
  } = formMethods;

  const { origin } = router.query as { origin?: LoginSignUpOrigins };

  /**
   * Maps the origin to the destination if we autologin
   */
  const originAutologinMap: Partial<Record<LoginSignUpOrigins, string>> = {
    [LoginSignUpOrigins.cart]: AppRoutes.CHECKOUT,
  };

  const afterActionCodeSignUpSuccessWithAutologin = () => {
    const destination = originAutologinMap[origin!] || AppRoutes.LOGIN;
    const afterSuccessUrl = new URL(CommonUtils.buildPath("/", router.locale, destination), location.origin);
    router.push(afterSuccessUrl.href);
  };

  const doSendVerification = async (
    data: Omit<KikCustomerSignUpWithActionCodeFields, "actionCode" | "procedure"> = filledData
  ) => {
    if ((timeLeft || 0) > 0) {
      return setIsActionCodeDialogOpen(!0);
    }

    const sendEmailVerificationRequest: KikCustomerSendEmailVerificationFields = pick(
      Object.keys(kikCustomerSendEmailVerificationFieldsSchema.shape) as (keyof Omit<
        KikCustomerSendEmailVerificationFields,
        "procedure"
      >)[],
      data
    ) as KikCustomerSendEmailVerificationFields;

    return sendEmailVerification(sendEmailVerificationRequest)
      .then(() => {
        resetTimer();
        setIsActionCodeDialogOpen(!0);
      })
      .catch((e) => {
        setIsActionCodeDialogOpen(!1);
        Logger.instance.error("SignUp: could not call sendEmailVerification", e);
      });
  };

  const doSignUpWithActionCode = (actionCode: string) => {
    return signUpCustomerWithActionCode(
      {
        ...filledData,
        actionCode,
      },
      router.query.origin as LoginSignUpOrigins
    )
      .then(() => {
        setIsActionCodeDialogOpen(!1);

        return login({
          ...pick(["EmailAddress", "password"], formMethods.getValues()),
        }).then(() => afterActionCodeSignUpSuccessWithAutologin());
      })
      .catch((err) => {
        Logger.instance.error("SignUp: sign-up with action code errored", {
          err,
          authError,
        });
      });
  };

  useEffect(() => {
    ApiUtils.mapBackendErrorsToForm<CustomerSignUpFields>(authError, setError);
  }, [authError, setError]);

  const onSubmit = async (data: Omit<KikCustomerSignUpWithActionCodeFields, "actionCode">) => {
    if (socialKey) {
      try {
        await signUpSocial(data as FirebaseCustomClaims, socialKey);
        await autoLogin();
        router.push(appRoutes.HOMEPAGE);
      } catch (err) {
        Logger.instance.error(`SignUp: signUpSocial failed for user ${data?.EmailAddress}`, err);
      }

      return;
    }
    setFilledData(data);

    return doSendVerification(data);
  };

  return (
    <FormProvider {...formMethods}>
      {!!socialKey && (
        <div>
          <p>{t("generic.socialLoginSignUpDisclaimer")}</p>
        </div>
      )}
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <input {...register("CountryID")} type="hidden" value={initialCountry} />
        <InputField
          {...register("FirstName")}
          defaultValue={inVals.FirstName}
          hidden={!!hiddenFields?.FirstName}
          type="text"
          label={t("generic.form.FirstName")}
          error={
            errors.FirstName?.message
              ? t(errors.FirstName?.message, {
                  fieldName: t("generic.form.FirstName"),
                  maxLength: ZodSchemas.firstNameMaxlength,
                })
              : ""
          }
          onClearErrors={() => clearErrors("FirstName")}
          maxLength={ZodSchemas.firstNameMaxlength}
        />
        <InputField
          {...register("LastName")}
          defaultValue={inVals.LastName}
          hidden={!!hiddenFields?.LastName}
          type="text"
          label={t("generic.form.LastName")}
          error={
            errors.LastName?.message
              ? t(errors.LastName?.message, {
                  fieldName: t("generic.form.LastName"),
                  maxLength: ZodSchemas.lastNameMaxlength,
                })
              : ""
          }
          onClearErrors={() => clearErrors("LastName")}
          maxLength={ZodSchemas.lastNameMaxlength}
        />
        <InputFieldDateMui
          control={control}
          name="DateOfBirth"
          label={t("generic.form.birthday")}
          error={
            errors.DateOfBirth?.message
              ? t(errors.DateOfBirth?.message, {
                  fieldName: t("generic.form.birthday"),
                  minimumAgeInYears: ZodSchemas.userMinimumAgeInYears,
                  maximumAgeInYears: ZodSchemas.userMaximumAgeInYears,
                })
              : ""
          }
          onClearErrors={() => clearErrors("DateOfBirth")}
          minimumDate={ZodSchemas.userBirthdayMinDto}
          maximumDate={ZodSchemas.userBirthdayMaxDto.minus({ day: 1 })}
        />
        <ControlledPhoneInput
          {...register("PhoneNumber")}
          control={control}
          label={t("generic.form.PhoneNumber")}
          placeholder={t("generic.form.PhoneNumber_placeholder")}
          maxLength={ZodSchemas.phoneNumberMaxlength}
          isOptional
          error={
            errors.PhoneNumber?.message
              ? t(errors.PhoneNumber?.message, {
                  fieldName: t("generic.form.PhoneNumber"),
                  maxLength: ZodSchemas.phoneNumberMaxlength,
                  minLength: ZodSchemas.phoneNumberMinlength,
                })
              : ""
          }
          onClearErrors={() => clearErrors("PhoneNumber")}
        />
        <InputField
          defaultValue={inVals.EmailAddress}
          hidden={!!hiddenFields?.EmailAddress}
          readOnly={!!socialKey || isSubmitting || isActionCodeDialogOpen}
          type="email"
          label={t("generic.form.EmailAddress")}
          error={
            errors.EmailAddress?.message
              ? t(errors.EmailAddress?.message, {
                  fieldName: t("generic.form.EmailAddress"),
                  maxLength: ZodSchemas.emailMaxlength,
                })
              : ""
          }
          {...register("EmailAddress")}
          onClearErrors={() => clearErrors("EmailAddress")}
          maxLength={ZodSchemas.emailMaxlength}
        />
        <SelectGeneric
          {...register("LanguageID")}
          defaultValue={inVals.LanguageID}
          hidden={!!inVals.LanguageID}
          label={t("generic.form.select_prefered_language")}
          options={availableLanguages}
        />
        {!socialKey && (
          <>
            <InputField
              {...register("password")}
              type={showPassword ? "text" : "password"}
              label={t("generic.form.password_label")}
              error={
                errors.password?.message
                  ? t(errors.password?.message, {
                      fieldName: t("generic.form.password_label"),
                      minLength: ZodSchemas.passwordMinlength,
                    })
                  : ""
              }
              buttonName={
                <Checkbox
                  label={t("generic.form.show_hide_password")}
                  variant={CheckboxVariants.visibility}
                  width={16}
                  height={16}
                  customStyles={{
                    icon: inputFieldStyles.iconvisibility,
                    container: inputFieldStyles.containerShowHidePassowrdCheckbox,
                    labelCheckbox: inputFieldStyles.labelShowHidePassowrdCheckbox,
                  }}
                  checked={showPassword}
                  onChange={() => setShowPassword(!showPassword)}
                />
              }
              onClearErrors={() => clearErrors("password")}
            />
            <InputField
              {...register("confirmPassword")}
              type={showPassword ? "text" : "password"}
              label={t("generic.form.confirm_password_label")}
              error={
                errors.confirmPassword?.message
                  ? t(errors.confirmPassword?.message, {
                      fieldName: t("generic.form.confirm_password_label"),
                      minLength: ZodSchemas.passwordMinlength,
                    })
                  : ""
              }
              buttonName={
                <Checkbox
                  label={t("generic.form.show_hide_password")}
                  variant={CheckboxVariants.visibility}
                  width={16}
                  height={16}
                  customStyles={{
                    icon: inputFieldStyles.iconvisibility,
                    container: inputFieldStyles.containerShowHidePassowrdCheckbox,
                    labelCheckbox: inputFieldStyles.labelShowHidePassowrdCheckbox,
                  }}
                  checked={showPassword}
                  onChange={() => setShowPassword(!showPassword)}
                />
              }
              onClearErrors={() => clearErrors("confirmPassword")}
            />
          </>
        )}
        <p className={styles.mandatory}>{t("generic.form.mandatory_long")}</p>

        <CustomerFlags styles={styles} />

        <Errors error={authError} style={{ gridColumn: breakpoint === "desktop" ? "2 span" : "1" }} />

        <Button
          variant="primary"
          type="submit"
          disabled={isSubmitting || isLoading}
          loading={isSubmitting || isLoading}
          className={styles.button}
        >
          {t("generic.create_account")}
        </Button>
      </form>
      <ActionCodeDialog onSubmit={doSignUpWithActionCode} onSendAnother={doSendVerification} />
    </FormProvider>
  );
}
